/**
 * WAV Audio Recorder
 * Optager audio i høj kvalitet WAV format ved hjælp af Web Audio API
 */

export class WAVRecorder {
  private audioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private recordedBuffers: Float32Array[] = [];
  private isRecording: boolean = false;
  private sampleRate: number = 48000;
  private stream: MediaStream | null = null;

  /**
   * Start optagelse fra MediaStream
   */
  async start(stream: MediaStream, sampleRate: number = 48000): Promise<void> {
    try {
      this.sampleRate = sampleRate;
      this.stream = stream;
      this.recordedBuffers = [];
      
      // Opret AudioContext
      this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
      
      // Opret source fra stream
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream);
      
      // Opret processor til at fange audio data
      const bufferSize = 4096;
      this.processor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      // Gem audio chunks
      this.processor.onaudioprocess = (e) => {
        if (!this.isRecording) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        const buffer = new Float32Array(inputData.length);
        buffer.set(inputData);
        this.recordedBuffers.push(buffer);
      };
      
      // Connect nodes
      this.mediaStreamSource.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
      
      this.isRecording = true;
      console.log('🎙️ WAV optagelse startet (høj kvalitet)');
    } catch (error) {
      console.error('Fejl ved start af WAV optagelse:', error);
      throw error;
    }
  }

  /**
   * Stop optagelse og returner WAV blob
   */
  async stop(): Promise<Blob> {
    this.isRecording = false;
    
    console.log('⏹️ Stopper WAV optagelse...');
    
    // Disconnect nodes
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
    
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
    
    // Convert buffers til WAV
    const wavBlob = this.exportWAV();
    console.log('✅ WAV fil oprettet:', wavBlob.size, 'bytes');
    
    return wavBlob;
  }

  /**
   * Konverter recorded buffers til WAV blob
   */
  private exportWAV(): Blob {
    // Merge alle buffers
    const totalLength = this.recordedBuffers.reduce((acc, buffer) => acc + buffer.length, 0);
    const mergedBuffer = new Float32Array(totalLength);
    
    let offset = 0;
    for (const buffer of this.recordedBuffers) {
      mergedBuffer.set(buffer, offset);
      offset += buffer.length;
    }
    
    // Convert float32 til int16 for WAV
    const int16Buffer = this.floatTo16BitPCM(mergedBuffer);
    
    // Opret WAV header
    const wavBuffer = this.createWAVBuffer(int16Buffer);
    
    return new Blob([wavBuffer], { type: 'audio/wav' });
  }

  /**
   * Convert Float32Array til Int16Array (PCM)
   */
  private floatTo16BitPCM(input: Float32Array): Int16Array {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return output;
  }

  /**
   * Opret WAV fil buffer med header
   */
  private createWAVBuffer(samples: Int16Array): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    // WAV Header
    // "RIFF" chunk descriptor
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    this.writeString(view, 8, 'WAVE');
    
    // "fmt " sub-chunk
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk size (16 for PCM)
    view.setUint16(20, 1, true); // Audio format (1 = PCM)
    view.setUint16(22, 1, true); // Number of channels (1 = mono)
    view.setUint32(24, this.sampleRate, true); // Sample rate
    view.setUint32(28, this.sampleRate * 2, true); // Byte rate
    view.setUint16(32, 2, true); // Block align
    view.setUint16(34, 16, true); // Bits per sample
    
    // "data" sub-chunk
    this.writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);
    
    // Write PCM samples
    const offset = 44;
    for (let i = 0; i < samples.length; i++) {
      view.setInt16(offset + i * 2, samples[i], true);
    }
    
    return buffer;
  }

  /**
   * Write string til DataView
   */
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Tjek om der er optagelse i gang
   */
  isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  /**
   * Få nuværende optagelseslængde i sekunder
   */
  getCurrentDuration(): number {
    if (!this.recordedBuffers.length) return 0;
    const totalSamples = this.recordedBuffers.reduce((acc, buffer) => acc + buffer.length, 0);
    return totalSamples / this.sampleRate;
  }
}

